/**
 * GA4 Measurement ID validation for the operator analytics passthrough (#463).
 *
 * Operators can route the booking-widget funnel into their OWN Google Analytics
 * 4 property by passing their Measurement ID (e.g. `G-XXXXXXXXXX`) into the
 * embedded widget. The ID is NOT PII — it is already public in the operator's
 * own site `<head>` — so it is safe to carry as a URL query param.
 *
 * SECURITY: a validated ID is only ever used as
 *   (a) the `id=` query-string value when constructing the gtag.js script src, and
 *   (b) the first argument to `gtag('config', id, …)`.
 * It is NEVER assigned to `innerHTML`, passed to `eval`, or written as any other
 * DOM attribute. The allow-list regex below is the single chokepoint that keeps
 * an attacker-controlled `?ga4MeasurementId=` value out of those sinks.
 */

declare global {
  interface Window {
    /** GA4 dataLayer — the queue gtag.js drains. */
    dataLayer?: unknown[];
    /** The gtag shim / gtag.js function (present only when a valid ID loaded). */
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 Measurement IDs are `G-` followed by 4–20 uppercase alphanumerics.
 * Anchored on both ends so nothing can be smuggled before/after the ID.
 */
const GA4_ID_RE = /^G-[A-Z0-9]{4,20}$/;

/**
 * Validate and normalize a raw GA4 Measurement ID candidate.
 *
 * @param raw - The untrusted candidate (e.g. a URL param or operator input).
 * @returns The normalized (trimmed, uppercased) ID when it matches the GA4
 *   format, or `null` for anything malformed/empty/nullish.
 */
export function parseGa4Id(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim().toUpperCase();
  return GA4_ID_RE.test(trimmed) ? trimmed : null;
}
